import {ApolloError} from '@sail/data';
import React from 'react';
import {view, Link} from '@sail/ui';
import type {FormatXMLElementFn} from '@sail/intl';
import {message, useIntl} from '@sail/intl';
import type {OpenapiAccountTypeEnum} from '@stripe-internal/data-v1/schema/types';
import {useMetrics} from '@sail/observability';
import {ErrorMessage, LoadingState} from '../../SharedComponents';
import {SupportLink} from '../../support';
import {IdentityVerification} from './IdentityVerification';
import useGetIdentityChallengeExternalAccountKeyLazyQuery from '../queries/useGetIdentityChallengeExternalAccountKeyLazyQuery';

type Props = {
  authAttemptId: string;
  session: string;
  onSuccess: () => void;
  retryVerification: () => void;
  accountType?: OpenapiAccountTypeEnum;
  overrideBranding?: boolean;
};

const messages = {
  verificationFailed: message<{
    TryAgainLink: FormatXMLElementFn;
    SupportLink: FormatXMLElementFn;
  }>({
    id: 'connectEmbedLib.identifyVerification.verificationFailed.md',
    description:
      'Error message that notifies the user that the Identity Verification failed, and to try again or contact support.',
    defaultMessage:
      'We were unable to be verify your identity. You may <TryAgainLink>try again</TryAgainLink>, or if you continue to have verification issues, we can help you <SupportLink>on our support website</SupportLink>.',
  }),

  internalError: message<{
    TryAgainLink: FormatXMLElementFn;
    SupportLink: FormatXMLElementFn;
  }>({
    id: 'connectEmbedLib.identifyVerification.internalError',
    description:
      'Error message that notifies the user that we had an internal error, and to try again or contact support.',
    defaultMessage:
      'We encountered an internal problem. You may <TryAgainLink>try again</TryAgainLink>, or if you continue to have verification issues, we can help you <SupportLink>on our support website</SupportLink>.',
  }),
};

const getIdentityVerificationErrorMessage = (
  error?: ApolloError | Error,
): typeof messages.verificationFailed | typeof messages.internalError => {
  if (
    error instanceof ApolloError &&
    error.graphQLErrors.find(
      (gqlError: any) => gqlError.code === 'identity_challenge_not_passed',
    )
  ) {
    return messages.verificationFailed;
  } else {
    return messages.internalError;
  }
};
export const IdentityVerificationWrapper = ({
  authAttemptId,
  session,
  onSuccess,
  accountType,
  retryVerification,
  overrideBranding,
}: Props) => {
  const intl = useIntl();
  const metrics = useMetrics();
  const [verificationError, setVerificationError] = React.useState<Error>();
  const [getKeyAttemptExceeded, setGetKeyAttemptExceeded] =
    React.useState<boolean>(false);
  const MAX_GET_KEY_ATTEMPT = 2;
  const DEFAULT_RETRY_DELAY_MS = 500;
  const [getIdentityChallengeKey, {error}] =
    useGetIdentityChallengeExternalAccountKeyLazyQuery();
  const [loading, setLoading] = React.useState<boolean>(false);

  React.useEffect(() => {
    metrics.increment(
      'connect.auth.external_account_verification.verification.form_view',
      {challenge_type: 'identity'},
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const wait = (ms: number) =>
    new Promise((r) => {
      setTimeout(r, ms);
    });

  const handleIdentityChallengeSuccess = React.useCallback(async () => {
    setLoading(true);
    const attemptGetKey = async (attempt: number) => {
      let succeeded = false;
      try {
        const {data} = await getIdentityChallengeKey({
          variables: {input: {auth_attempt_id: authAttemptId}},
        });

        if (
          data?.v1ConnectIdentityChallengesAuthAttemptIdExternalAccountKey?.key
        ) {
          succeeded = true;
          onSuccess();
        }
      } catch {
        // Because of https://github.com/apollographql/apollo-client/issues/9669, we need to catch the calls to lazy queries
        // otherwise they fail in tests. We can remove this once we upgrade @apollo/client
      } finally {
        if (!succeeded) {
          if (attempt === MAX_GET_KEY_ATTEMPT) {
            setGetKeyAttemptExceeded(true);
          } else {
            const waitTime = 2 ** attempt * DEFAULT_RETRY_DELAY_MS;
            await wait(waitTime);
            await attemptGetKey(attempt + 1);
          }
        }
      }
    };

    await attemptGetKey(1);
    setLoading(false);
  }, [getIdentityChallengeKey, authAttemptId, onSuccess]);

  const handleRetry = React.useCallback(() => {
    setVerificationError(undefined);
    setGetKeyAttemptExceeded(false);
    retryVerification();
  }, [retryVerification]);

  if ((getKeyAttemptExceeded && error) || verificationError) {
    return (
      <ErrorMessage>
        <view.p css={{font: 'body.small'}}>
          {intl.formatMessage(
            getIdentityVerificationErrorMessage(
              (getKeyAttemptExceeded ? error : undefined) || verificationError,
            ),
            {
              TryAgainLink: (text: string) => (
                <Link onPress={handleRetry}>{text}</Link>
              ),
              SupportLink: (text: string) => (
                <SupportLink accountType={accountType}>{text}</SupportLink>
              ),
            },
          )}
        </view.p>
      </ErrorMessage>
    );
  } else if (loading) {
    return <LoadingState />;
  } else {
    return (
      <IdentityVerification
        verificationSession={session}
        onCompleted={() => {
          handleIdentityChallengeSuccess();
        }}
        onError={(error) => setVerificationError(error)}
        overrideBranding={overrideBranding}
      />
    );
  }
};
